import React, { type FC } from 'react'
import { Box, Modal, Pill } from '@moonpig/launchpad-components'
import { useExperiment } from '@moonpig/web-core-eppo/browser'
import {
  breakpointDown,
  breakpoint,
  css,
  styled,
} from '@moonpig/launchpad-utils'
import { ProductContext, ProductInfoProduct, UpsellProduct } from '../../types'
import { isCardProduct } from '../../utils/isCardProduct'
import { transformPill } from '../../utils/transformPill'
import {
  ProductRecommendationAnchorProvider,
  ProductStoreProvider,
} from '../../contexts/product'
import { useProductInitialVariantSelectionState } from '../../utils/useProductInitialVariantSelectionState'
import { ProductTrackingProvider } from '../../contexts/tracking'
import { OnConfigureModulesCallback } from '../../modules/types'
import { ModuleLayoutContainer } from '../../modules/LayoutContainer/ModuleLayoutContainer'
import { ScrollToTop } from './ScrollToTop'
import { isDigitalGiftCard } from '../../utils/isDigitalGift/isDigitalGift'
import { hasMultipleVariants } from '../../utils/hasMultipleVariants'
import { SimplifiedModalBanner } from './components/SimplifiedModalBanner/SimplifiedModalBanner'
import { useCanNavigateInModal } from '../../utils/useCanNavigateInModal'

type Props = {
  product: ProductInfoProduct
  upsellProduct?: UpsellProduct
  triggerElementRef?: React.RefObject<HTMLAnchorElement | null>
  productContext: ProductContext
  isOpen: boolean
  onDismiss: () => void
  onConfigureModules: OnConfigureModulesCallback
}

// Temporary solution to remove x axis paddings for the simplified-modal-and-pdp experiment
// An alternative option is to create a modal component from scratch as shown here:
// https://docs.launchpad.moonpig.io/?path=/docs/components-modal--docs#building-blocks
const StyledSimplifiedProductModal = styled(Modal)`
  ${breakpoint('md')} {
    [data-testid='quick-view-body'] {
      display: flex;
      flex-direction: column;
      min-height: 0;
      overflow: hidden;
    }
  }

  ${breakpointDown('md')} {
    [data-testid='quick-view-body'] {
      padding-left: 0;
      padding-right: 0;
    }
  }
`

const StyledModalComponent = styled(Modal)<{ $scrollWholeModal: boolean }>`
  ${({ $scrollWholeModal }) =>
    $scrollWholeModal &&
    css`
      ${breakpoint('md')} {
        overflow-y: auto;
        padding-top: 5dvh;
        padding-bottom: 5dvh;

        [role='dialog'] {
          max-height: none;
        }

        [data-testid='quick-view-body'] {
          overflow: visible;
        }
      }
    `}
`

export const ProductModal: FC<Props> = ({
  product,
  upsellProduct,
  triggerElementRef,
  productContext,
  isOpen,
  onDismiss,
  onConfigureModules,
}) => {
  const { variantKey } = useProductInitialVariantSelectionState()

  const isCard = product.category && isCardProduct(product.category.department)

  const hideInitialVariantSelection =
    hasMultipleVariants(product.variants) &&
    isDigitalGiftCard(product.category.department)

  const pill = product.primaryProductPill
    ? transformPill(product.primaryProductPill)
    : undefined

  const isSimplifiedModal =
    useExperiment('simplified-modal-and-pdp', {
      fallback: 'control',
    }) === 'treatment'

  const canNavigateInModal = useCanNavigateInModal()
  const isMoreLikeThisEnabled = isCard && canNavigateInModal

  const isSimplifiedCardModal = isSimplifiedModal && isCard

  const banner = isSimplifiedCardModal ? (
    <SimplifiedModalBanner product={product} variantKey={variantKey} />
  ) : (
    <Box flexGrow={2} alignSelf="center">
      {isCard && !!pill && (
        <Box ml={6}>
          <Box mr={{ xs: 4, md: 6 }}>
            <Pill
              data-testid="card-banner-pill"
              label={pill.displayLabel}
              variant={pill.displayVariant}
            />
          </Box>
        </Box>
      )}
    </Box>
  )

  const modalProps = {
    label: product?.title,
    isOpen,
    hasCloseButton: true,
    onDismiss,
    testId: 'quick-view',
    bannerContent: banner,
    // eslint-disable-next-line no-warning-comments
    // TODO: Launchpad need to support nullable triggerRef for R19
    triggerRef: triggerElementRef as React.RefObject<HTMLAnchorElement>,
  }

  const productContent = (
    <ProductStoreProvider
      initialProduct={product}
      initialVariantKey={variantKey}
      upsellProduct={upsellProduct}
      productContext={productContext}
      hideInitialVariantSelection={hideInitialVariantSelection}
    >
      <ProductTrackingProvider
        pageLocation="product details"
        pageComponent="popup"
      >
        <ScrollToTop scrollWholeModal={isMoreLikeThisEnabled}>
          <ModuleLayoutContainer
            onConfigureModules={onConfigureModules}
            context={productContext}
          />
        </ScrollToTop>
      </ProductTrackingProvider>
    </ProductStoreProvider>
  )

  const modalContent = canNavigateInModal ? (
    <ProductRecommendationAnchorProvider product={product}>
      {productContent}
    </ProductRecommendationAnchorProvider>
  ) : (
    productContent
  )

  if (isSimplifiedCardModal) {
    return (
      <StyledSimplifiedProductModal {...modalProps}>
        {modalContent}
      </StyledSimplifiedProductModal>
    )
  }

  return (
    <StyledModalComponent
      {...modalProps}
      $scrollWholeModal={isMoreLikeThisEnabled}
    >
      {modalContent}
    </StyledModalComponent>
  )
}
